import { normalizeSearch, searchTokens, toolSearchAliases } from './searchAliases';
import type { ToolCategory, ToolDefinition } from './types';

export type CatalogCategory = ToolCategory | 'all' | 'favorites';
export type CatalogSort = 'smart' | 'name' | 'recent';

export interface RankedTool {
  score: number;
  tool: ToolDefinition;
}

interface CatalogOptions {
  category: CatalogCategory;
  favorites: ReadonlySet<string>;
  query: string;
  recent: readonly string[];
  sort: CatalogSort;
}

export function selectCatalogTools(items: readonly ToolDefinition[], options: CatalogOptions): ToolDefinition[] {
  const keyword = options.query.trim().toLocaleLowerCase();
  const originalRank = new Map(items.map((tool, index) => [tool.id, index]));
  const recentRank = new Map(options.recent.map((id, index) => [id, index]));
  const rank = (toolId: string) => recentRank.get(toolId) ?? Number.MAX_SAFE_INTEGER;

  return items
    .filter((tool) => {
      const matchesCategory = options.category === 'all'
        || (options.category === 'favorites' ? options.favorites.has(tool.id) : tool.category === options.category);
      const matchesQuery = !keyword || scoreToolSearch(tool, keyword) > 0;
      return matchesCategory && matchesQuery;
    })
    .sort((left, right) => {
      if (options.sort === 'name') return left.title.localeCompare(right.title, 'zh-CN');
      if (options.sort === 'recent') {
        return rank(left.id) - rank(right.id)
          || Number(options.favorites.has(right.id)) - Number(options.favorites.has(left.id))
          || (originalRank.get(left.id) ?? 0) - (originalRank.get(right.id) ?? 0);
      }
      if (keyword) {
        const scoreDifference = scoreToolSearch(right, keyword) - scoreToolSearch(left, keyword);
        if (scoreDifference) return scoreDifference;
      }
      return Number(options.favorites.has(right.id)) - Number(options.favorites.has(left.id))
        || rank(left.id) - rank(right.id)
        || (originalRank.get(left.id) ?? 0) - (originalRank.get(right.id) ?? 0);
    });
}

export function rankToolSearch(items: readonly ToolDefinition[], query: string, limit = 10): ToolDefinition[] {
  const keyword = query.trim().toLocaleLowerCase();
  if (!keyword) return items.slice(0, limit);
  return items
    .map((tool, index) => ({ tool, score: scoreToolSearch(tool, keyword), index }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, limit)
    .map((entry) => entry.tool);
}

export function scoreToolSearch(tool: ToolDefinition, query: string): number {
  const keyword = normalizeSearch(query);
  if (!keyword) return 1;
  const title = normalizeSearch(tool.title);
  const keywords = [...tool.keywords, ...(toolSearchAliases[tool.parentToolId || tool.id] || [])].map(normalizeSearch);
  const summary = normalizeSearch(tool.summary);
  const compact = (value: string) => value.replace(/[^\p{L}\p{N}+#]/gu, '');
  const needle = compact(keyword), titleCompact = compact(title);
  if (!needle) return 0;
  if (titleCompact === needle) return 120;
  if (titleCompact.startsWith(needle)) return 100;
  if (titleCompact.includes(needle)) return 88;
  if (keywords.some((item) => compact(item) === needle)) return 84;
  if (keywords.some((item) => compact(item).includes(needle))) return 68;
  const phrases = [title, ...keywords, summary], tokens = searchTokens(keyword);
  if (tokens.length && tokens.every((token) => phrases.some((phrase) => compact(phrase).includes(compact(token))))) return 54;
  if (needle.length < 5) return 0;
  if (isSubsequence(needle, titleCompact)) return 36;
  if (keywords.some((item) => isSubsequence(needle, compact(item)))) return 30;
  return 0;
}

function isSubsequence(needle: string, haystack: string): boolean {
  if (!needle) return true;
  let cursor = 0;
  for (const character of haystack) {
    if (character === needle[cursor]) cursor += 1;
    if (cursor === needle.length) return true;
  }
  return false;
}

export function paginateCatalog<T>(items: readonly T[], requestedPage: number, pageSize: number) {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const page = Math.min(Math.max(1, requestedPage), pageCount);
  const start = (page - 1) * pageSize;
  return { items: items.slice(start, start + pageSize), page, pageCount, total: items.length };
}
